import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { Separator } from '../../../components/ui/separator';
import { ArrowLeft, Smartphone, MapPin, Clock, Settings, Wifi, Signal, Zap, Activity, Router, Cpu } from 'lucide-react';
import { toast } from 'react-toastify';
import { useApi } from '../../../hooks/useApi';
import { Skeleton } from '../../../components/ui/skeleton';
import ThemeToggle from '../../../components/ThemeToggle';

export default function DeviceDetails() {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { get } = useApi();

  useEffect(() => {
    fetchDeviceDetails();
  }, [deviceId]);

  const fetchDeviceDetails = async () => {
    try {
      setIsLoading(true);
      
      const response = await get(`/devices/${deviceId}`);
      
      
      // Handle the API response structure
      if (response?.success && response?.data) {
        setDevice(response.data);
      } else if (response?.data) {
        setDevice(response.data);
      } else {
        setDevice(null);
        toast.error('Device not found');
      }
      
    } catch (error) {
      
      let errorMessage = 'An error occurred while fetching device details. Please try again.';
      
      if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please login again.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Device not found.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      setDevice(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/devices');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100';
      case 'In-Pool':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100';
      case 'Inactive':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100';
      default:
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 max-w-6xl bg-background text-foreground min-h-screen">
        <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 w-10 rounded" />
            <div>
              <Skeleton className="h-6 w-48 mb-2" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
          <Skeleton className="h-10 w-32" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-0 shadow-none bg-muted/50">
            <CardHeader>
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-64" />
            </CardHeader>
            <CardContent className="space-y-4">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="flex justify-between items-center">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-32" />
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-none bg-muted/50">
            <CardHeader>
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-64" />
            </CardHeader>
            <CardContent className="space-y-4">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="flex justify-between items-center">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-32" />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!device) {
    return (
      <div className="container mx-auto p-6 max-w-6xl bg-background text-foreground min-h-screen">
        <div className="flex flex-col items-center justify-center h-64">
          <Smartphone className="h-16 w-16 text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">Device Not Found</h2>
          <p className="text-muted-foreground mb-4">The requested device could not be found.</p>
          <Button onClick={handleBack} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Devices
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl bg-background text-foreground min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <Button onClick={handleBack} variant="ghost" size="sm" className="mb-4 sm:mb-0">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Devices
          </Button>
        </div>
        
        {/* Device Header Card */}
        <Card className="border-0 shadow-lg bg-muted/50">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              {/* Device Avatar */}
              <Avatar className="h-20 w-20 border-4 border-white dark:border-gray-800 shadow-lg">
                <AvatarImage src={`https://api.dicebear.com/7.x/shapes/svg?seed=${device.deviceName}&backgroundColor=3b82f6`} />
                <AvatarFallback className="bg-blue-500 text-white text-lg font-bold">
                  {device.deviceName?.substring(0, 2).toUpperCase() || 'DV'}
                </AvatarFallback>
              </Avatar>
              
              {/* Device Info */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
                  <h1 className="text-xl font-bold text-foreground">{device.deviceName}</h1>
                  <Badge className={`${getStatusColor(device.deviceStatus)} border-0 px-3 py-1 text-sm font-medium`}>
                    <Activity className="w-3 h-3 mr-1" />
                    {device.deviceStatus}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">IMEI:</span>
                    <span className=" font-medium">{device.imeiNumber}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Router className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">GPS:</span>
                    <span className=" font-medium">{device.gpsCardNo}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Signal className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">SIM:</span>
                    <span className=" font-medium">{device.simCard}</span>
                  </div>
                </div>
              </div>
              
              {/* Quick Stats */}
              <div className="flex flex-col gap-2 text-right">
                
               
                <div className="text-xs text-muted-foreground mt-2">
                  Created {formatDate(device.createdOn).split(',')[0]}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Technical Specifications */}
        <Card className="border-0 shadow-md bg-muted/50 hover:shadow-lg transition-shadow">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400">
                  <Cpu className="w-5 h-5" />
                </AvatarFallback>
              </Avatar>
              <div>
                <CardTitle className="text-lg">Technical Specifications</CardTitle>
                <CardDescription>Hardware and identification details</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 gap-4">
              <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 text-xs">
                      <Smartphone className="w-4 h-4" />
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium">IMEI Number</span>
                </div>
                <span className="text-sm  bg-muted px-2 py-1 rounded">{device.imeiNumber}</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400 text-xs">
                      <Router className="w-4 h-4" />
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium">GPS Card</span>
                </div>
                <span className="text-sm  bg-muted px-2 py-1 rounded">{device.gpsCardNo}</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-400 text-xs">
                      <Settings className="w-4 h-4" />
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium">GPS Serial</span>
                </div>
                <span className="text-sm  bg-muted px-2 py-1 rounded">{device.gpsSerialNo}</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400 text-xs">
                      <Signal className="w-4 h-4" />
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium">SIM Card</span>
                </div>
                <span className="text-sm  bg-muted px-2 py-1 rounded">{device.simCard}</span>
              </div>
              
              {device.firmwareVersion && (
                <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-400 text-xs">
                        <Zap className="w-4 h-4" />
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium">Firmware</span>
                  </div>
                  <span className="text-sm bg-muted px-2 py-1 rounded">{device.firmwareVersion}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Location & Tracking */}
        {/* <Card className="border-0 shadow-none bg-muted/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Location & Tracking
            </CardTitle>
            <CardDescription>
              Current location and tracking information
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-muted-foreground">Latitude:</span>
              <span className="text-sm ">{device.currentLatitude || 'N/A'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-muted-foreground">Longitude:</span>
              <span className="text-sm ">{device.currentLongitude || 'N/A'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-muted-foreground">Altitude:</span>
              <span className="text-sm">{device.currentAltitude ? `${device.currentAltitude} m` : 'N/A'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-muted-foreground">Direction:</span>
              <span className="text-sm">{device.currentDirection ? `${device.currentDirection}°` : 'N/A'}</span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-sm font-medium text-muted-foreground">Address:</span>
              <span className="text-sm text-right max-w-[200px]">{device.currentAddress || 'N/A'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-muted-foreground">Last Reported:</span>
              <span className="text-sm">{formatDate(device.lastReportedAt)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-muted-foreground">Previous Report:</span>
              <span className="text-sm">{formatDate(device.previousReportedAt)}</span>
            </div>
          </CardContent>
        </Card> */}

        {/* Device Type Information */}
        {device.deviceType && (
          <Card className="border-0 shadow-md bg-muted/50 hover:shadow-lg transition-shadow">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white">
                    {device.deviceType.name?.substring(0, 2).toUpperCase() || 'DT'}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <CardTitle className="text-lg">Device Type</CardTitle>
                  <CardDescription>{device.deviceType.name} - {device.deviceType.manufacturer}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-3">
                <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className="bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 text-xs">
                        <Settings className="w-3 h-3" />
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium">Model</span>
                  </div>
                  <span className="text-sm font-medium">{device.deviceType.modelName}</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className="bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 text-xs">
                        <Cpu className="w-3 h-3" />
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium">Firmware</span>
                  </div>
                  <span className="text-sm bg-muted px-2 py-1 rounded">{device.deviceType.firmwareVersion}</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className="bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-400 text-xs">
                        <Zap className="w-3 h-3" />
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium">Hardware</span>
                  </div>
                  <span className="text-sm bg-muted px-2 py-1 rounded">{device.deviceType.hardwareVersion}</span>
                </div>
                
                <Separator className="my-4" />
                
                <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className="bg-emerald-100 dark:bg-emerald-900 text-emerald-600 dark:text-emerald-400 text-xs">
                        <Activity className="w-3 h-3" />
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium">Type Status</span>
                  </div>
                  <Badge className={`${getStatusColor(device.deviceType.status)} border-0 px-3 py-1`}>
                    {device.deviceType.status}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Distribution & Timestamps */}
        <Card className="border-0 shadow-md bg-muted/50 hover:shadow-lg transition-shadow ">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-gradient-to-br from-emerald-500 to-teal-500 text-white">
                  <Clock className="w-5 h-5" />
                </AvatarFallback>
              </Avatar>
              <div>
                <CardTitle className="text-lg">Distribution & Timeline</CardTitle>
                <CardDescription>Distribution status and important timestamps</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-4 bg-background/50 rounded-lg border-l-4 border-emerald-500">
                <div className="flex items-center gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-emerald-100 dark:bg-emerald-900 text-emerald-600 dark:text-emerald-400 text-xs">
                      <Wifi className="w-4 h-4" />
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <span className="text-sm font-medium block">Distributor Received</span>
                    <span className="text-xs text-muted-foreground">{formatDate(device.distributorReceivedDate)}</span>
                  </div>
                </div>
                <Badge className={`${device.isDistributorReceived ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100' : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100'} border-0 px-3 py-1`}>
                  {device.isDistributorReceived ? 'Yes' : 'No'}
                </Badge>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-background/50 rounded-lg border-l-4 border-blue-500">
                <div className="flex items-center gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 text-xs">
                      <Clock className="w-4 h-4" />
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <span className="text-sm font-medium block">Created On</span>
                    <span className="text-xs text-muted-foreground">Device registration</span>
                  </div>
                </div>
                <span className="text-sm font-medium">{formatDate(device.createdOn)}</span>
              </div>
              
              {device.modifiedOn && (
                <div className="flex items-center justify-between p-4 bg-background/50 rounded-lg border-l-4 border-orange-500">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-400 text-xs">
                        <Settings className="w-4 h-4" />
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <span className="text-sm font-medium block">Last Modified</span>
                      <span className="text-xs text-muted-foreground">Recent update</span>
                    </div>
                  </div>
                  <span className="text-sm font-medium">{formatDate(device.modifiedOn)}</span>
                </div>
              )}
            </div>
            
            {device.notes && (
              <div className="mt-6">
                <Separator className="mb-4" />
                <div className="flex items-start gap-3 p-4 bg-background/50 rounded-lg border-l-4 border-indigo-500">
                  <Avatar className="h-8 w-8 mt-1">
                    <AvatarFallback className="bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-400 text-xs">
                      📝
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <span className="text-sm font-medium text-foreground block mb-2">Device Notes</span>
                    <p className="text-sm text-muted-foreground leading-relaxed bg-muted/30 p-3 rounded border-0">
                      {device.notes}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
